import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AuthService,
  LogService,
  PrintService,
  ProfileService,
  StyleService,
} from '../../services';
import { environment } from './../../../environments/environment';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as uuid from 'uuid';
import moment from 'moment';
import { LogSource, LogType } from '../../models/common';
import { InsightsLogsService } from '../../services/insights-logs.service';

@Component({
  selector: 'app-confirm-redeem-modal',
  templateUrl: './confirm-redeem-modal.component.html',
  styleUrls: ['./confirm-redeem-modal.component.scss']
})
export class ConfirmRedeemModalComponent implements OnInit {

  public printOnScreen: boolean = false;
  public isTestMode = false;

  public useNewVersion: boolean = false;
  public useNewVersionKiosks: string = "";

  constructor(
    private readonly dialogRef: MatDialogRef<any>,
    private readonly printService: PrintService,
    private readonly profileService: ProfileService,
    private readonly snackbar: MatSnackBar,
    private readonly ins: InsightsLogsService,
    private readonly logService: LogService,
    private readonly authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public styleService: StyleService
  ) { }

  ngOnInit() {

    const voucherCfg = this.data?.currentTemplate?.configurations?.voucher;
    this.printOnScreen = !!voucherCfg?.printOnScreen;
    this.isTestMode    = !!voucherCfg?.printOnScreen?.isTestMode;  

    this.useNewVersion = voucherCfg?.advPrint?.isEnabled;
    this.useNewVersionKiosks = voucherCfg?.advPrint?.kioskDeviceIds;

    this.printService.isNewPrintVersion = this.useNewVersion && 
      (
        this.useNewVersionKiosks.split(",").findIndex((dev) => dev.trim() === this.authService.deviceId) > -1 || 
        !this.useNewVersionKiosks
      )

  }
  
  ngAfterViewInit(): void {
    let backDropStyles: CSSStyleDeclaration = (document.querySelectorAll('.backDropClass')[0] as any)?.style;
    backDropStyles.background = this.data.styles.overlay || '#00000052';
    const dialogSurface = document.querySelectorAll('.mat-mdc-dialog-surface')[0];
    let contentStyles: CSSStyleDeclaration = (dialogSurface.getElementsByClassName('content')[0] as HTMLElement).style;
    (dialogSurface as HTMLElement).style.borderRadius = this.data.styles.border.borderRadius + 'px';
    (dialogSurface as HTMLElement).style.borderColor = this.data.styles.border.borderColor;
    (dialogSurface as HTMLElement).style.borderWidth = this.data.styles.border.borderWidth + 'px';
    contentStyles.background = this.data.styles.background.color;
    contentStyles.backgroundImage = `url(${this.data.styles.background.imageLink})`;
  }

  public get isPrinting() {
    return this.printService.isPrinting;
  }

  public closeDialog() {
    this.dialogRef.close();
  }

public async confirmRedemption() {

  const voucherCfg    = this.data?.currentTemplate?.configurations?.voucher;
  const printOnScreen = !!voucherCfg?.printOnScreen;
  const isTestMode    = !!voucherCfg?.printOnScreen?.isTestMode;

  const needOnScreen  = printOnScreen && isTestMode;

  //const previewWindow = needOnScreen ? window.open('', '_blank') : null;
  let previewWindow: Window | null = null;

  const isPrintRequired = !!this.data?.currentTemplate?.templateConfig?.isPrintVoucherRequired;


  // if (needOnScreen) previewWindow = window.open('', '_blank');
  // this.printPage(previewWindow);

  this.logRedeemContext('confirmRedemption:start', {
    needOnScreen,
    printOnScreen,
    isTestMode,
    isPrintRequired,
  });

  if (needOnScreen) {

    previewWindow = window.open('', '_blank');
    this.printPage(previewWindow);

    // If print-on-screen is selected, NEVER fall back to printer
    if (!previewWindow) {
      this.logService.writeLog(
        'Print-on-screen selected but window.open was blocked. Exiting without printing.',
        LogType.ERROR,
        LogSource.PRINT
      );
      this.printService.setIsPrinting(false, 'Print on screen blocked');
      return;
    }
  }

  // as per Alex FreePlay must never print (V2 or legacy)
  if (isPrintRequired && this.isFreePlayRedeem()) {
    this.logService.writeLog(
      `BLOCKED: FreePlay attempted to print. playerId=${this.profileService.playerProfile.playerId}, selectedRedeemId=${this.data?.selectedRedeemId}, type=${this.data?.selectedRedeemOption?.type}, conversionType=${this.data?.conversionType}`,
      LogType.ERROR,
      LogSource.REDEMPTION
    );

    this.closeDialog();
    try {
      await this.data.callback();
    } catch {
      // rejection handled by onBadRequest in redemption component
    } finally {
      try { previewWindow?.close(); } catch {}
      this.printService.setIsPrinting(false, 'Voucher blocked (FreePlay)');
    }
    return;
  }

  const html = this.getSelectedVoucherHtml();
  const isV2 = this.getV2RedeemOptionIndex() >= 0;

  // If this is V2 and html is empty -> skip printing entirely (even if legacy exists)
  if (isPrintRequired && isV2 && !html.length) {
    this.closeDialog();
    try {
      await this.data.callback();
    } catch {
      // rejection handled by onBadRequest in redemption component
    } finally {
      try { previewWindow?.close(); } catch {}
      this.printService.setIsPrinting(false, 'Voucher skipped (V2 empty HTML)');
    }
    return;
  }

  if (isPrintRequired) {
    this.closeDialog();

    //added logs to trace callback() lifecycle for isPrintRequired path
    this.logService.writeLog(
      `[ConfirmRedeemModal] isPrintRequired=true, calling callback()`,
      LogType.ERROR,
      LogSource.REDEMPTION
    );

    (this.data.callback() as Promise<boolean | string>)
      .then(async () => {

        this.logService.writeLog(
          `[ConfirmRedeemModal] callback() resolved successfully, calling printPage`,
          LogType.ERROR,
          LogSource.REDEMPTION
        );

        //print state moved here from redemption components; modal has full context (freePlay, empty HTML, isPrintRequired) to decide if printing happens
        this.printService.setIsPrinting(true, 'Confirm Redeem Modal before printPage');
        
        await this.printPage(previewWindow);

        this.logService.writeLog(`
          User ${this.profileService.playerProfile.playerId} converted
          ${this.data.pointToReedem} points to ${this.data?.conversionType} ${this.data.redeemValue}$
        `, LogType.DEBUG, LogSource.REDEMPTION);
      })
      .catch((err) => {

        this.logService.writeLog(
          `[ConfirmRedeemModal] callback() rejected: ${JSON.stringify(err)}`,
          LogType.ERROR,
          LogSource.REDEMPTION
        );

         //print state moved here from redemption components; modal has full context (freePlay, empty HTML, isPrintRequired) to decide if printing happens
        try { previewWindow?.close(); } catch {}
        this.printService.setIsPrinting(false, 'Confirm Redeem Modal callback failed before printPage');
        
        this.logService.writeLog(
          `User ${this.profileService.playerProfile.playerId} tried to convert ${this.data.pointToReedem} points to ${this.data?.conversionType}: ${this.data.redeemValue}
          Error:${JSON.stringify(err)}`,
          LogType.ERROR,
          LogSource.REDEMPTION
        );
      });

    return;
  }
  // Not print required
  this.closeDialog();
  try {
    await this.data.callback();
    this.logService.writeLog(
      `User ${this.profileService.playerProfile.playerId} converted ${this.data.pointToReedem} points to ${this.data?.conversionType} ${this.data.redeemValue}$`,
      LogType.DEBUG,
      LogSource.REDEMPTION
    );
  } catch (error) {
    this.logService.writeLog(
      `User ${this.profileService.playerProfile.playerId} tried to convert ${this.data.pointToReedem} points to ${this.data?.conversionType}: ${this.data.redeemValue}
      Error:${JSON.stringify(error)}`,
      LogType.ERROR,
      LogSource.REDEMPTION
    );
  }
}


  public async printPage(previewWindow?: Window | null): Promise<void> {
  const rawTemplate = this.getSelectedVoucherHtml();

  //OC-486: Added extra logging and a specific block for FreePlay redeems, as per Alex's request that they should never print (regardless of V1/V2 or print-on-screen)
  if (this.isFreePlayRedeem()) {
    this.logService.writeLog(
      `BLOCKED: FreePlay printPage call. playerId=${this.profileService.playerProfile.playerId}, selectedRedeemId=${this.data?.selectedRedeemId}, type=${this.data?.selectedRedeemOption?.type}`,
      LogType.ERROR,
      LogSource.PRINT
    );
    try { previewWindow?.close(); } catch {}
    this.printService.setIsPrinting(false, 'Voucher printing blocked (FreePlay)');
    return;
  }


  // single skip condition for BOTH modes
  if (!rawTemplate.length) {
    try { previewWindow?.close(); } catch {}
    this.printService.setIsPrinting(false, 'Voucher skipped (empty HTML)');
    return;
  }

  const typePlain =
    this.data.voucherType
      ?? (this.data.conversionType ? String(this.data.conversionType).replace(/\.$/, '') : '');

  const vars = {
    pageIndex: this.data.pageIndex,
    templateIndex: this.data.templateIndex,
    voucherId: uuid.v4(),
    playerId: this.data.playerId,
    convertedPoints: this.data.redeemValue,
    convertedValue: this.data.pointToReedem,
    promotionName: this.data.conversionType,
    expirationDate: this.getExpiratonDate(),
    patronName: this.getPatronName(),
    convertedType: typePlain,
  };

  const { htmlPath, voucherBasePathOverride } = this.getVoucherRequestConfig();

  const page = this.printService.preparePage(rawTemplate, vars, htmlPath, undefined, voucherBasePathOverride
  );

  if (previewWindow) {
    try {
      previewWindow.opener = null;
      previewWindow.location.href = page;
      previewWindow.focus();
    } finally {
      this.printService.setIsPrinting(false, 'Print on screen');
    }
    return;
  }

  this.logService.writeLog('Start printing redeem voucher', LogType.TRACE, LogSource.PRINT);

  return (this.printService.isNewPrintVersion ? this.printService.printPage_V2(page) : this.printService.printPage(page)).then(() => {
    this.printService.setIsPrinting(false, "Confirm Redeem Modal printPage success");
  })
    .catch((e) => {
      this.logRedeemContext('printPage:catch', {
        message: e?.message,
        error: JSON.stringify(e),
      });

      // Snapshot what the kiosk actually received from builder
      this.logRedeemOptionsList('printPage:catch redeemOptions snapshot');

      
      if (e?.message === 'Timeout') {
        this.logService.writeLog('Start printing redeem voucher failed due to timeout', LogType.ERROR, LogSource.PRINT);
      }
      this.printService.setIsPrinting(false, "Confirm Redeem Modal printPage err");

      this.snackbar.openFromComponent(SnackbarComponent, {
        panelClass: 'error',
        duration: 5000,
        horizontalPosition: 'end',
        data: {
          type: 'error',
          message: 'Print failed, but your point was converted.',
        },
      });
    });
}

  private resolveVoucherHtmlPath(): 'html' | 'htmlMatchPlay' | 'htmlDirectBet' {
    let id = (this.data?.selectedRedeemId as string | undefined) ?? '';
    //if (id === 'freeSlotPlay') id = 'freePlay';
    if (id === 'matchPlay') return 'htmlMatchPlay';
    if (id === 'directBet') return 'htmlDirectBet';

    const t = this.data?.selectedRedeemOption?.type as string | undefined;
    if (t === 'matchPlay') return 'htmlMatchPlay';
    if (t === 'directBet') return 'htmlDirectBet';

    return 'html'; // default for v1 and freePlay/anything else
  }

  private getPatronName(): string {
    if (this.profileService.isUcSecondVersion) {
      return `${this.profileService.playerProfile.playerProfile.firstName} ${this.profileService.playerProfile.playerProfile.lastName}`;
    }
    return `${this.profileService.playerProfile.name.first} ${this.profileService.playerProfile.name.last}`;
  }

  private getExpiratonDate() {
    if (!!this.data.currentTemplate.configurations.voucher.expirationDays) {
      return this.getDate(true, true, this.data.currentTemplate.configurations.voucher.expirationDays);
    }
    return 'Unexpired';
  }

  private getDate(isFull: boolean, is24Hours?: boolean, expirationDate?: number): string {
    const now = new Date();
    if (!!expirationDate) {
      now.setDate(now.getDate() + Number(expirationDate))
    }

    const days = now.getDate();
    const month = now.getMonth() + 1;
    const year = now.getFullYear().toString().substr(-2);
    const hours = is24Hours ? now.getHours() : now.getHours() % 12 || 12;
    const minutes = now.getMinutes();
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const AMPM = now.getHours() >= 12 ? 'PM' : 'AM';

    let preparedString = `${month}/${days}/${year}`;

    if (isFull) {
      preparedString += ` ${hours}:${minutes}:${seconds}`;
    }

    if (!is24Hours) {
      preparedString += ` ${AMPM}`;
    }

    return preparedString;
  }

   //OC-486:- replaced  - logging added for OC-486
private getSelectedVoucherHtml1(): string {

  const idx = this.getV2RedeemOptionIndex();

  // --- V2 dynamic ---
  if (idx >= 0) {
    // empty means "do not print"
    const cfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];

    //as per Alex, no voucher for FreePlay in V2
    if (String(cfg[idx]?.type ?? '').trim() === 'freePlay') return '';

    return String(cfg[idx]?.voucherHtml ?? '').trim();
  }

  // --- V1 legacy ---
  const voucherCfg = this.data?.currentTemplate?.configurations?.voucher || {};
  const path = this.resolveVoucherHtmlPath();
  return String(voucherCfg?.[path] ?? voucherCfg?.html ?? '').trim();
}

private getSelectedVoucherHtml(): string {
  const idx = this.getV2RedeemOptionIndex();

  this.logRedeemContext('getSelectedVoucherHtml:start', { idx });

  // --- V2 dynamic ---
  if (idx >= 0) {
    const cfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];
    const opt = cfg[idx];

    const typeRaw = String(opt?.type ?? '').trim();
    const typeNorm = typeRaw.toLowerCase();
    const voucherHtml = String(opt?.voucherHtml ?? '').trim();
    const len = voucherHtml.length;

    this.logRedeemContext('getSelectedVoucherHtml:V2', {
      idx,
      typeRaw,
      voucherHtmlLen: len,
      hasVoucherHtml: len > 0,
    });

    // full payload snapshot 
    if (len === 0) {
      this.logRedeemOptionsList('getSelectedVoucherHtml:V2 empty voucherHtml');
    }

    //as per Alex, no voucher for FreePlay in V2
    if (typeRaw === 'freePlay') {
      this.logRedeemContext('getSelectedVoucherHtml:V2 freePlay forced-empty', { idx, typeRaw });
      return '';
    }

    return voucherHtml;
  }

  // --- V1 legacy ---
  const voucherCfg = this.data?.currentTemplate?.configurations?.voucher || {};
  const path = this.resolveVoucherHtmlPath();
  const legacy = String(voucherCfg?.[path] ?? voucherCfg?.html ?? '').trim();

  this.logRedeemContext('getSelectedVoucherHtml:V1', {
    path,
    legacyHtmlLen: legacy.length,
    hasLegacyHtml: legacy.length > 0,
    availableKeys: Object.keys(voucherCfg ?? {}).slice(0, 30),
  });

  return legacy;
}


private isV2DynamicSelection(): boolean {
  const selectedOpt = this.data?.selectedRedeemOption;
  const selectedId = selectedOpt?.id != null ? String(selectedOpt.id) : '';

  const redeemOptionsCfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];

  if (!selectedId) return false;

  const idx = redeemOptionsCfg.findIndex((o: any) => String(o?.id ?? '') === selectedId);
  return idx >= 0;
}

//OC-486- logging added for OC-486
private getVoucherRequestConfig(): { htmlPath: string; voucherBasePathOverride?: string } {
  const idx = this.getV2RedeemOptionIndex();

  const isV2 = idx >= 0;
  const htmlPath = isV2 ? 'voucherHtml' : this.resolveVoucherHtmlPath();

  const voucherBasePathOverride = isV2
    ? `pageLinks[${this.data.pageIndex}].templateSettings[${this.data.templateIndex}].templateConfig.redeemOptions[${idx}]`
    : undefined;

  this.logRedeemContext('getVoucherRequestConfig', {
    idx,
    isV2,
    htmlPath,
    voucherBasePathOverride,
  });

  if (idx >= 0) {
    return {
      htmlPath: 'voucherHtml',
      voucherBasePathOverride:
        `pageLinks[${this.data.pageIndex}].templateSettings[${this.data.templateIndex}].templateConfig.redeemOptions[${idx}]`,
    };
  }

  return {
    htmlPath: this.resolveVoucherHtmlPath(),
    voucherBasePathOverride: undefined,
  };
}

//OC-486 - to be replaced - logging added for OC-486
private getV2RedeemOptionIndex_original(): number {

  const selectedId = this.data?.selectedRedeemOption?.id != null
    ? String(this.data.selectedRedeemOption.id)
    : '';

  if (!selectedId) return -1;

  const cfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];
  return cfg.findIndex(o => String(o?.id ?? '') === selectedId);
}

//OC-486 - logging added to original for OC-486
private getV2RedeemOptionIndex(): number {

  const selectedOpt = this.data?.selectedRedeemOption;
  const selectedId =
    selectedOpt?.id != null ? String(selectedOpt.id) : '';

  const selectedType =
    selectedOpt?.type != null ? String(selectedOpt.type) : '';
//configurations
  const cfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];

  // ENTRY LOG
  this.logService.writeLog(
    `[getV2RedeemOptionIndex] start :: selectedId=${selectedId}, selectedType=${selectedType}, cfgCount=${cfg.length}`,
    LogType.DEBUG,
    LogSource.REDEMPTION
  );

  // Also send to AppInsights for better tracing
  this.ins?.logTrace?.('getV2RedeemOptionIndex:start', {
    selectedId,
    selectedType,
    cfgCount: cfg.length,
  });

  if (!selectedId) {
    this.logService.writeLog(
      `[getV2RedeemOptionIndex] no selectedId → returning -1`,
      LogType.DEBUG,
      LogSource.REDEMPTION
    );
    return -1;
  }

  const idx = cfg.findIndex(o => String(o?.id ?? '') === selectedId);

  // RESULT LOG
  this.logService.writeLog(
    `[getV2RedeemOptionIndex] result :: idx=${idx}`,
    LogType.DEBUG,
    LogSource.REDEMPTION
  );

  this.ins?.logTrace?.('getV2RedeemOptionIndex:result', {
    selectedId,
    idx,
  });

  // if not found
  if (idx < 0) {
    const ids = cfg.map(o => String(o?.id ?? '')).slice(0, 20);

    this.logService.writeLog(
      `[getV2RedeemOptionIndex] NOT FOUND :: selectedId=${selectedId}, cfgIds(first20)=${JSON.stringify(ids)}`,
      LogType.ERROR,
      LogSource.REDEMPTION
    );

    this.ins?.logTrace?.('getV2RedeemOptionIndex:not_found', {
      selectedId,
      selectedType,
      cfgCount: cfg.length,
      cfgIdsFirst20: JSON.stringify(ids),
    });
  }

  return idx;
}

//OC-486 - new to do later
private getV2RedeemOptionIndex2(): number {
  const selected = this.data?.selectedRedeemOption;
  const selectedId = selected?.id != null ? String(selected.id) : '';
  const selectedType = selected?.type != null ? String(selected.type) : '';

  const cfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];

  if (!cfg.length) return -1;

  // ID match, but allow type fallback
  let idx = -1;

  if (selectedId) {
    idx = cfg.findIndex(o => String(o?.id ?? '') === selectedId);
  }

  if (idx < 0 && selectedType) {
    idx = cfg.findIndex(o => String(o?.type ?? '') === selectedType);
  }

  return idx;
}

//check for freeplay redeem
private isFreePlayRedeem(): boolean {
  const id = (this.data?.selectedRedeemId ?? '').toString().toLowerCase();
  const type = (this.data?.selectedRedeemOption?.type ?? '').toString().toLowerCase();
  const conv = (this.data?.conversionType ?? '').toString().toLowerCase();
  const voucherType = (this.data?.voucherType ?? '').toString().toLowerCase();

  const normalize = (v: string) => v.replace(/\s+/g, '');

  const isFreePlayToken = (v: string) => {
    const n = normalize(v);
    return n === 'freeplay' || n === 'freeslotplay';
  };

  return (
    isFreePlayToken(id) ||
    isFreePlayToken(type) ||
    isFreePlayToken(conv) ||
    isFreePlayToken(voucherType)
  );
}

private logRedeemContext(tag: string, extra?: any) {
  const selected = this.data?.selectedRedeemOption;
  //configuration
  const cfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];

  const snapshot = {
    tag,
    ts: new Date().toISOString(),
    playerId: this.profileService?.playerProfile?.playerId,
    pageIndex: this.data?.pageIndex,
    templateIndex: this.data?.templateIndex,
    selectedRedeemId: this.data?.selectedRedeemId,
    selectedOpt: selected
      ? { id: String(selected?.id ?? ''), type: String(selected?.type ?? ''), name: selected?.name }
      : null,
    isPrintRequired: !!this.data?.currentTemplate?.templateConfig?.isPrintVoucherRequired,
    redeemOptionsCount: cfg.length,
    redeemOptionIds: cfg.slice(0, 20).map(o => String(o?.id ?? '')), // limit to first 20, so it is not too big
  };

  this.logService.writeLog(
    `[ConfirmRedeemModal] ${tag} :: ${JSON.stringify({ ...snapshot, extra })}`,
    LogType.DEBUG,
    LogSource.REDEMPTION
  );

  this.ins?.logTrace?.(`[ConfirmRedeemModal] ${tag}`, { ...snapshot, extra });
}

private logRedeemOptionsList(tag: string) {
  //configuration
  const cfg = (this.data?.currentTemplate?.templateConfig?.redeemOptions ?? []) as any[];

  const slim = cfg.map((o, i) => ({
    i,
    id: String(o?.id ?? ''),
    type: String(o?.type ?? ''),
    name: o?.name,
    voucherHtmlLen: String(o?.voucherHtml ?? '').trim().length,
    hasVoucherHtml: !!String(o?.voucherHtml ?? '').trim().length,
  }));

  // LogService 
  this.logService.writeLog(
    `[ConfirmRedeemModal] ${tag} redeemOptions=${JSON.stringify(slim)}`,
    LogType.DEBUG,
    LogSource.REDEMPTION
  );

  // If list is too large, send count + first 20 only
  this.ins?.logTrace?.(`[ConfirmRedeemModal] ${tag} redeemOptions snapshot`, {
    count: slim.length,
    first20: JSON.stringify(slim.slice(0, 20)),
  });
}

 //OC-523 - new method to check for dining redeem, to be used later
  public isDiningRedeem(): boolean {
    const type = (this.data?.selectedRedeemOption?.type ?? '').toString().trim().toLowerCase();
    return type === 'dining';
  }
  //end OC-523

}